import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/PermissionGate';
import { Loader2, Search, RefreshCw, ExternalLink, ChevronRight, ChevronDown,
  Package, DollarSign, ClipboardCheck, Truck, FileText, Building2, AlertTriangle,
  CheckCircle2, Clock, Plus, Edit, PackageCheck, Boxes, X, ArrowRight } from 'lucide-react';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import UniversalFilterBar from '@/components/procurement/UniversalFilterBar';
import POFormDialog from '@/components/procurement/POFormDialog';
import ExcelWorkflowMenu from '@/components/procurement/ExcelWorkflowMenu';
import useCrossTabDiffToast from '@/hooks/useCrossTabDiffToast';

const STATUS_COL = {
  DELAYED:  { bg: 'var(--tone-red-bg)', fg: 'var(--tone-red-text)' },
  AT_RISK:  { bg: 'var(--tone-amber-bg)', fg: 'var(--tone-amber-text)' },
  ON_TRACK: { bg: 'var(--tone-green-bg)', fg: 'var(--tone-green-text)' },
  DONE:     { bg: 'var(--tone-blue-bg)', fg: 'var(--tone-blue-text)' },
  NOT_STARTED: { bg: 'var(--tone-slate-bg)', fg: 'var(--text-muted)' },
};
const GATE_COL = {
  DONE: '#1E4E8C', ON_TRACK: '#22C55E', AT_RISK: '#F59E0B',
  DELAYED: '#EF4444', NOT_STARTED: '#CBD5E1',
};

function fmt(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${Number(n).toLocaleString()}`;
}

function LineItemRow({ line, projectId, onOpen }) {
  const st = STATUS_COL[line.status] || STATUS_COL.NOT_STARTED;
  return (
    <tr onClick={() => onOpen(line.mciLineNumber)} className="cursor-pointer hover:bg-black/5"
      style={{ borderTop: '1px solid var(--border-color)' }}
      data-testid={`po-line-${line.mciLineNumber}`}>
      <td className="pl-10 pr-2 py-1.5"></td>
      <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: 'var(--tone-navy-text)' }}>{line.poLineItem}</td>
      <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: 'var(--tone-navy-text)' }}>{line.mciLineNumber}</td>
      <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}
        data-testid={`po-mat-code-${line.mciLineNumber}`}
        title={line.materialCategory === 'BULK' ? 'Bulk item code' : 'Tag number'}>
        {line.materialCode || '—'}
      </td>
      <td className="px-2 py-1.5 max-w-[200px] truncate text-[11px]" style={{ color: 'var(--text-primary)' }} title={line.materialDescription}>
        {line.materialDescription}
      </td>
      <td className="px-2 py-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>{line.discipline}</td>
      <td className="px-2 py-1.5 text-right text-[10px] font-mono whitespace-nowrap" style={{ color: 'var(--text-primary)' }}
        data-testid={`po-line-qty-${line.mciLineNumber}`}>
        {line.quantity != null
          ? <><b>{line.quantity}</b> {line.unit || ''}</>
          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td className="px-2 py-1.5 text-right text-[10px] font-mono whitespace-nowrap"
        data-testid={`po-grn-${line.mciLineNumber}`}
        style={{ color: line.grnQty > 0 ? '#1E4E8C' : 'var(--text-muted)' }}
        title="GRN Qty delivered">
        {line.quantity != null ? (line.grnQty || 0) : '—'}
      </td>
      <td className="px-2 py-1.5 text-right text-[10px] font-mono whitespace-nowrap"
        data-testid={`po-iqc-pending-${line.mciLineNumber}`}
        style={{ color: (line.iqcQty || 0) > 0 ? '#F59E0B' : 'var(--text-muted)' }}
        title="Pending IQC inspection">
        {line.iqcQty || 0}
      </td>
      <td className="px-2 py-1.5 text-right text-[10px] font-mono whitespace-nowrap"
        data-testid={`po-iqc-ok-${line.mciLineNumber}`}
        style={{ color: (line.acceptedQty || 0) > 0 ? '#10B981' : 'var(--text-muted)' }}
        title="IQC Accepted (passed inspection)">
        {line.acceptedQty || 0}
      </td>
      <td className="px-2 py-1.5 text-right text-[10px] font-mono whitespace-nowrap"
        data-testid={`po-iqc-rej-${line.mciLineNumber}`}
        style={{ color: (line.rejectedQty || 0) > 0 ? '#EF4444' : 'var(--text-muted)' }}
        title="IQC Rejected (failed inspection)">
        {line.rejectedQty || 0}
      </td>
      <td className="px-2 py-1.5 text-right text-[10px] font-mono whitespace-nowrap"
        data-testid={`po-remain-${line.mciLineNumber}`}
        style={{ color: line.remainingQty > 0 ? '#EF4444' : '#10B981' }}
        title="Balance yet to arrive">
        {line.remainingQty || 0}
      </td>
      <td className="px-2 py-1.5 text-right font-mono text-[10px]" style={{ color: 'var(--text-primary)' }}>{fmt(line.poValue)}</td>
      <td className="px-2 py-1.5 text-center font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{(line.requiredAtSiteDate || '').slice(0, 10) || '—'}</td>
      <td className="px-2 py-1.5 text-center font-mono text-[10px]"
        style={{ color: line.bufferDays == null ? 'var(--text-muted)'
                  : line.bufferDays < 0 ? '#EF4444'
                  : line.bufferDays < 15 ? '#F59E0B' : '#10B981' }}>
        {line.bufferDays != null ? `${line.bufferDays > 0 ? '+' : ''}${line.bufferDays}d` : '—'}
      </td>
      {/* Gate strip */}
      <td className="px-2 py-1.5">
        <div className="flex gap-0.5" title={`${line.postOrderGates.length} gates`}>
          {line.postOrderGates.map((g, i) => (
            <div key={i} title={`${g.stageGateName} · ${g.status}`}
              className="w-3 h-3 rounded-sm"
              style={{ background: GATE_COL[g.status] || GATE_COL.NOT_STARTED }} />
          ))}
        </div>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <div className="flex-1 h-1.5 rounded-full min-w-[50px]" style={{ background: 'var(--card-bg-alt)' }}>
            <div className="h-full rounded-full" style={{
              width: `${line.progressPercent || 0}%`,
              background: (line.progressPercent || 0) >= 80 ? '#10B981' : (line.progressPercent || 0) >= 40 ? '#F59E0B' : '#EF4444'
            }} />
          </div>
          <span className="text-[9px] font-mono w-8 text-right" style={{ color: 'var(--text-muted)' }}>{line.progressPercent}%</span>
        </div>
      </td>
      <td className="px-2 py-1.5 text-center">
        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
          style={{ background: st.bg, color: st.fg }}>
          {line.status}
        </span>
      </td>
      <td className="px-2 py-1.5 text-center"><ExternalLink className="w-3 h-3 inline" style={{ color: 'var(--tone-navy-text)' }} /></td>
    </tr>
  );
}

function POCard({ po, projectId, onOpen, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const st = STATUS_COL[po.status] || STATUS_COL.NOT_STARTED;
  return (
    <>
      <tr className="cursor-pointer hover:bg-black/5"
        style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg-alt)' }}
        onClick={() => setOpen(!open)} data-testid={`po-${po.poNumber}`}>
        <td className="px-2 py-2 w-7">{open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</td>
        <td colSpan={1} className="px-2 py-2 font-mono font-bold" style={{ color: 'var(--tone-navy-text)' }}>
          <ClipboardCheck className="w-3 h-3 inline mr-1" />{po.poNumber}
        </td>
        <td className="px-2 py-2 text-[10px]" style={{ color: 'var(--text-muted)' }}
          data-testid={`po-mci-count-${po.poNumber}`}>
          {po.linesTotal} MCI{po.linesTotal === 1 ? '' : 's'}
        </td>
        <td className="px-2 py-2 text-[11px]" style={{ color: 'var(--text-primary)' }}>
          <Building2 className="w-3 h-3 inline mr-1" />{po.vendor || '— vendor —'}
        </td>
        <td className="px-2 py-2 text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{(po.poDate || '').slice(0, 10) || '—'}</td>
        <td className="px-2 py-2 text-right text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
          {po.linesTotal} line{po.linesTotal === 1 ? '' : 's'}
        </td>
        <td className="px-2 py-2 text-right text-[10px] font-mono" style={{ color: 'var(--tone-navy-text)' }}>
          {po.lineItems.filter(li => (li.grnQty || 0) > 0).length}
        </td>
        <td className="px-2 py-2 text-right text-[10px] font-mono" style={{ color: '#F59E0B' }}>
          {po.lineItems.filter(li => (li.iqcQty || 0) > 0).length}
        </td>
        <td className="px-2 py-2 text-right text-[10px] font-mono" style={{ color: '#10B981' }}>
          {po.lineItems.filter(li => (li.acceptedQty || 0) > 0).length}
        </td>
        <td className="px-2 py-2 text-right text-[10px] font-mono" style={{ color: '#EF4444' }}>
          {po.lineItems.filter(li => (li.rejectedQty || 0) > 0).length}
        </td>
        <td className="px-2 py-2 text-right text-[10px] font-mono" style={{ color: '#EF4444' }}>
          {po.lineItems.filter(li => (li.remainingQty || 0) > 0).length}
        </td>
        <td className="px-2 py-2 text-right font-mono font-bold" style={{ color: '#F26B1F' }}>{fmt(po.totalValue)}</td>
        <td className="px-2 py-2 text-center text-[10px] font-bold"
          style={{ color: po.otdPercent >= 80 ? '#10B981' : po.otdPercent >= 60 ? '#F59E0B' : '#EF4444' }}>
          OTD {po.otdPercent}%
        </td>
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2 text-center">
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: st.bg, color: st.fg }}>
            {po.status}
          </span>
        </td>
        <td className="px-2 py-2 text-center">
          {po.poDocumentRef && (
            <a href={po.poDocumentRef} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              data-testid={`po-doc-${po.poNumber}`}>
              <FileText className="w-3.5 h-3.5 inline" style={{ color: 'var(--tone-navy-text)' }} />
            </a>
          )}
        </td>
      </tr>
      {open && po.lineItems.map(li => (
        <LineItemRow key={li.mciLineNumber} line={li} projectId={projectId} onOpen={onOpen} />
      ))}
    </>
  );
}

export default function POPerformance({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [catFilter, setCatFilter] = useState('ALL');
  const [uomFilter, setUomFilter] = useState('ALL');
  const [prioFilter, setPrioFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [drillMci, setDrillMci] = useState(null);
  const [poFormOpen, setPoFormOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [consolidateOpen, setConsolidateOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (search) params.append('search', search);
      const { data } = await api.get(`/v1/projects/${projectId}/mci/control-tower/po-performance?${params}`);
      setData(data);
    } catch (e) { toast.error('Failed to load PO Performance'); }
    setLoading(false);
  }, [projectId, statusFilter, search]);

  useEffect(() => {
    fetchData();
    const h = () => fetchData();
    window.addEventListener('mci-register-updated', h);
    return () => window.removeEventListener('mci-register-updated', h);
  }, [fetchData]);
  useCrossTabDiffToast('po-performance');

  const filteredPos = useMemo(() => {
    if (!data) return [];
    let arr = data.pos || [];
    if (catFilter !== 'ALL') {
      arr = arr
        .map(po => ({ ...po, lineItems: po.lineItems.filter(li => (li.materialCategory || '').toUpperCase() === catFilter) }))
        .filter(po => po.lineItems.length > 0);
    }
    if (uomFilter && uomFilter !== 'ALL') {
      arr = arr
        .map(po => ({ ...po, lineItems: po.lineItems.filter(li => (li.unit || '').toLowerCase() === uomFilter.toLowerCase()) }))
        .filter(po => po.lineItems.length > 0);
    }
    if (prioFilter && prioFilter !== 'ALL') {
      arr = arr
        .map(po => ({ ...po, lineItems: po.lineItems.filter(li => (li.priority || '').toUpperCase() === prioFilter) }))
        .filter(po => po.lineItems.length > 0);
    }
    return arr;
  }, [data, catFilter, uomFilter, prioFilter]);

  const kpis = useMemo(() => {
    if (!data) return {};
    const arr = data.pos || [];
    return {
      count: arr.length, lines: data.totalLines || 0,
      value: data.totalValue || 0,
      delayed: arr.filter(p => p.status === 'DELAYED').length,
      atRisk:  arr.filter(p => p.status === 'AT_RISK').length,
      onTrack: arr.filter(p => p.status === 'ON_TRACK' || p.status === 'DONE').length,
    };
  }, [data]);

  if (loading && !data) return <div className="flex items-center justify-center py-20">
    <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--tone-navy-text)' }} /></div>;

  const btnActive = { background: '#1E4E8C', color: '#ffffff' };
  const btnIdle   = { background: 'var(--card-bg)', color: 'var(--text-muted)' };

  return (
    <div className="space-y-4" data-testid="po-performance">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { l: 'Total POs',     v: kpis.count,   c: 'var(--tone-navy-text)', I: ClipboardCheck },
          { l: 'PO Line Items', v: kpis.lines,   c: 'var(--tone-navy-text)', I: Package },
          { l: 'Total Value',   v: fmt(kpis.value), c: '#F26B1F', I: DollarSign },
          { l: 'Delayed',       v: kpis.delayed, c: '#EF4444', I: AlertTriangle },
          { l: 'At Risk',       v: kpis.atRisk,  c: '#F59E0B', I: Clock },
          { l: 'On Track',      v: kpis.onTrack, c: '#10B981', I: CheckCircle2 },
        ].map((k, i) => {
          const I = k.I;
          return (
            <div key={i} className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
              data-testid={`po-kpi-${i}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>
                <I className="w-3 h-3" style={{ color: k.c }} />{k.l}
              </div>
              <div className="text-xl font-bold mt-1" style={{ color: k.c }}>{k.v}</div>
            </div>
          );
        })}
      </div>

      <UniversalFilterBar
        search={search} onSearchChange={setSearch} searchPlaceholder="Search PO # · vendor · line description"
        category={catFilter} onCategoryChange={setCatFilter}
        categoryOptions={[{ value:'ALL', label:'ALL' }, { value:'BULK', label:'BULK' }, { value:'TAGGED', label:'TAGGED' }]}
        priority={prioFilter} onPriorityChange={setPrioFilter}
        priorityOptions={[
          { value:'ALL', label:'ALL' }, { value:'CRITICAL', label:'CRIT' },
          { value:'HIGH', label:'HIGH' }, { value:'NORMAL', label:'NORMAL' },
          { value:'LOW', label:'LOW' },
        ]}
        phase={statusFilter} onPhaseChange={setStatusFilter}
        phaseOptions={[
          { value:'ALL', label:'ALL' }, { value:'DELAYED', label:'DELAYED' },
          { value:'AT_RISK', label:'AT RISK' }, { value:'ON_TRACK', label:'ON TRACK' },
          { value:'DONE', label:'DONE' },
        ]}
        uom={uomFilter} onUomChange={setUomFilter}
        uomOptions={[
          { value:'ALL', label:'ALL' }, { value:'kg', label:'kg' },
          { value:'ton', label:'ton' }, { value:'NOS', label:'NOS' },
          { value:'m', label:'m' }, { value:'LOT', label:'LOT' },
        ]}
        totalCount={(filteredPos || []).length} totalLabel="POs"
        right={
          <div className="flex items-center gap-1">
            <ExcelWorkflowMenu
              testIdPrefix="po-excel"
              endpoints={{
                label: 'Purchase Orders',
                template: `/v1/projects/${projectId}/mci/po-master/template.xlsx`,
                templateName: 'po-master-template.xlsx',
                importUrl: `/v1/projects/${projectId}/mci/po-master/import`,
                exportUrl: `/v1/projects/${projectId}/mci/po-master/export.xlsx`,
                exportName: `pos-${projectId}.xlsx`,
              }}
              onImported={fetchData}
            />
            <Button size="sm" onClick={() => setConsolidateOpen(true)}
              data-testid="consolidate-inventory-btn"
              className="text-white font-semibold"
              style={{ background: '#10B981' }}
              title="Move every IQC-Accepted qty into Material Tracking → Inventory">
              <PackageCheck className="w-3.5 h-3.5 mr-1" /> Consolidate to Inventory
            </Button>
            <PermissionGate module="MCI" action="create" fallback={null}>
              <Button size="sm" onClick={() => { setEditingPo(null); setPoFormOpen(true); }}
                data-testid="po-new-btn"
                className="text-white font-semibold" style={{ background: '#F26B1F' }}>
                <Plus className="w-3.5 h-3.5 mr-1" /> New PO
              </Button>
            </PermissionGate>
            <Button size="sm" variant="ghost" onClick={fetchData} data-testid="po-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        }
      />

      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="w-7"></th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Line</th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>PO / MCI</th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Material Code</th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Vendor · Description</th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>PO Date · Disc</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Scope Qty</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }} title="GRN Qty (delivered)">GRN Qty</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: '#F59E0B' }} title="Awaiting IQC inspection (GRN − Accepted − Rejected)">IQC Pending</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: '#10B981' }} title="IQC Accepted">IQC OK</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: '#EF4444' }} title="IQC Rejected">IQC Rej</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Remaining</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Value</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Lines / RAS</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>OTD / Buffer</th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Gate Strip</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Progress</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Status</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Doc</th>
            </tr>
          </thead>
          <tbody>
            {(filteredPos || []).map(p => (
              <POCard key={p.poNumber} po={p} projectId={projectId} onOpen={setDrillMci}
                defaultOpen={(filteredPos || []).length <= 2} />
            ))}
            {(filteredPos || []).length === 0 && (
              <tr><td colSpan={12} className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                No POs match the current filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drillMci && (
        <MciPackageDrawer projectId={projectId} mci={drillMci}
          onClose={() => { setDrillMci(null); fetchData(); }} />
      )}
      {poFormOpen && (
        <POFormDialog projectId={projectId} po={editingPo}
          onClose={() => { setPoFormOpen(false); setEditingPo(null); }}
          onSaved={() => fetchData()} />
      )}
      {consolidateOpen && (
        <ConsolidateToInventoryModal projectId={projectId}
          onClose={() => setConsolidateOpen(false)}
          onDone={() => fetchData()} />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Consolidate-to-Inventory modal
// ─────────────────────────────────────────────────────────────────────
function ConsolidateToInventoryModal({ projectId, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [phase, setPhase] = useState('PREVIEW');   // PREVIEW · RUNNING · DONE · ERROR
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/material-units/consolidate-preview`);
        setPreview(data);
      } catch (e) { toast.error('Preview failed'); }
      setBusy(false);
    })();
  }, [projectId]);

  const run = async () => {
    setPhase('RUNNING'); setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mtx/material-units/sync-from-iqc`);
      setResult(data);
      setPhase('DONE');
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
      toast.success(`${data.mintedUnits} item${data.mintedUnits === 1 ? '' : 's'} moved to Inventory`);
      onDone?.();
    } catch (e) {
      setPhase('ERROR');
      toast.error(e?.response?.data?.detail || 'Consolidation failed');
    }
    setBusy(false);
  };

  const moved = (result?.trace || []).filter(t => t.status === 'MOVED_TO_INVENTORY');
  const skipped = (result?.trace || []).filter(t => t.status === 'SKIPPED_ALREADY_CONSOLIDATED');

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 flex items-center justify-center p-4" onClick={onClose}
      data-testid="consolidate-inventory-modal">
      <div className="w-full max-w-2xl max-h-[85vh] rounded-xl shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <Boxes className="w-5 h-5" style={{ color: '#10B981' }} />
          <div className="flex-1">
            <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Consolidate IQC-Accepted → Inventory</div>
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              Every IQC-Accepted qty on a PO line becomes a live inventory unit in Material Tracking.
              Already-consolidated receipts are skipped automatically.
            </div>
          </div>
          <button onClick={onClose} data-testid="consolidate-close" style={{ color: 'var(--text-muted)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {busy && !preview && !result && (
            <div className="flex items-center justify-center py-10 gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-5 h-5 animate-spin" /> Analysing PO receipts…
            </div>
          )}

          {phase === 'PREVIEW' && preview && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="consolidate-preview-kpis">
                <div className="p-3 rounded-lg" style={{ background: '#10B98115', border: '1px solid #10B98140' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--tone-green-text)' }}>Will move to Inventory</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: '#10B981' }}>{preview.pendingReceiptCount}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>receipts · {preview.pendingQty} qty</div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Already consolidated</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text-muted)' }}>{preview.alreadyConsolidatedReceipts}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>receipts · {preview.alreadyConsolidatedQty} qty · will skip</div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#1E4E8C15', border: '1px solid #1E4E8C40' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--tone-blue-text)' }}>PO lines to touch</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--tone-navy-text)' }}>{preview.pendingLines.length}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{preview.pendingLines.length === 0 ? 'nothing new' : 'to consolidate'}</div>
                </div>
              </div>

              {preview.pendingLines.length === 0 ? (
                <div className="p-4 rounded-lg text-center text-[11px]"
                  style={{ background: 'var(--tone-green-bg)', border: '1px solid #86EFAC', color: 'var(--tone-green-text)' }}
                  data-testid="consolidate-nothing-todo">
                  <CheckCircle2 className="w-5 h-5 mx-auto mb-1" />
                  <b>Nothing new to consolidate.</b> All {preview.alreadyConsolidatedReceipts} IQC-Accepted receipts are already visible in Material Tracking → Inventory.
                </div>
              ) : (
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                  <table className="w-full text-[11px]">
                    <thead style={{ background: 'var(--card-bg-alt)' }}>
                      <tr>
                        <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>MCI</th>
                        <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Description</th>
                        <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Source</th>
                        <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Rack</th>
                        <th className="p-1.5 text-right" style={{ color: 'var(--text-muted)' }}>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.pendingLines.slice(0, 200).flatMap(l => l.receipts.map(r => (
                        <tr key={`${l.mciLineNumber}-${r.receiptNumber}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td className="p-1.5 font-mono" style={{ color: 'var(--tone-navy-text)' }}>{l.mciLineNumber}</td>
                          <td className="p-1.5 truncate max-w-[240px]" title={l.materialDescription} style={{ color: 'var(--text-primary)' }}>{l.materialDescription}</td>
                          <td className="p-1.5">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{
                              background: l.materialSource === 'OWNER' ? '#1E4E8C22' : '#F26B1F22',
                              color: l.materialSource === 'OWNER' ? 'var(--tone-blue-text)' : 'var(--tone-orange-text)',
                            }}>{l.materialSource || 'CONTRACTOR'}</span>
                          </td>
                          <td className="p-1.5 font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{r.acceptedLocation || '—'}</td>
                          <td className="p-1.5 text-right font-mono" style={{ color: '#10B981' }}>{r.acceptedQty} <span style={{ color: 'var(--text-muted)' }}>{r.unit}</span></td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {phase === 'RUNNING' && (
            <div className="flex items-center justify-center py-16 gap-2" style={{ color: 'var(--text-primary)' }}>
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#10B981' }} />
              <div>
                <div className="font-semibold">Moving items to Inventory…</div>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Materialising {preview?.pendingReceiptCount || '…'} IQC-Accepted receipt(s) into MaterialUnits with QR + RFID tags.
                </div>
              </div>
            </div>
          )}

          {phase === 'DONE' && result && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="consolidate-result-kpis">
                <div className="p-3 rounded-lg" style={{ background: '#10B98115', border: '1px solid #10B98140' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--tone-green-text)' }}>Moved to Inventory</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: '#10B981' }}>{result.mintedUnits}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>units · {result.mintedQty} qty</div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Skipped (already there)</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text-muted)' }}>{result.skippedAlreadyConsolidated}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>duplicate-safe</div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#1E4E8C15', border: '1px solid #1E4E8C40' }}>
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--tone-blue-text)' }}>Total processed</div>
                  <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--tone-navy-text)' }}>{(result.trace || []).length}</div>
                </div>
              </div>
              {/* Trace ledger */}
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}
                data-testid="consolidate-trace">
                <table className="w-full text-[11px]">
                  <thead style={{ background: 'var(--card-bg-alt)' }}>
                    <tr>
                      <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>MCI · Receipt</th>
                      <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Description</th>
                      <th className="p-1.5 text-left" style={{ color: 'var(--text-muted)' }}>Rack</th>
                      <th className="p-1.5 text-right" style={{ color: 'var(--text-muted)' }}>Qty</th>
                      <th className="p-1.5 text-center" style={{ color: 'var(--text-muted)' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...moved, ...skipped].slice(0, 500).map((t, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td className="p-1.5 font-mono text-[10px]" style={{ color: 'var(--tone-navy-text)' }}>{t.mciLineNumber} · {t.receiptNumber}</td>
                        <td className="p-1.5 truncate max-w-[220px]" title={t.materialDescription} style={{ color: 'var(--text-primary)' }}>{t.materialDescription}</td>
                        <td className="p-1.5 font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{t.location || '—'}</td>
                        <td className="p-1.5 text-right font-mono" style={{ color: t.status === 'MOVED_TO_INVENTORY' ? '#10B981' : 'var(--text-muted)' }}>
                          {t.qty} <span style={{ color: 'var(--text-muted)' }}>{t.unit}</span>
                        </td>
                        <td className="p-1.5 text-center">
                          {t.status === 'MOVED_TO_INVENTORY' ? (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold"
                              style={{ background: 'var(--tone-green-bg)', color: 'var(--tone-green-text)' }}>
                              <ArrowRight className="w-2.5 h-2.5" /> MOVED
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold"
                              style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                              — SKIPPED
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
          {phase === 'PREVIEW' && (
            <>
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="consolidate-cancel">Cancel</Button>
              <Button size="sm" onClick={run}
                disabled={busy || !preview || preview.pendingReceiptCount === 0}
                className="text-white font-semibold"
                style={{ background: '#10B981' }}
                data-testid="consolidate-run">
                <PackageCheck className="w-3.5 h-3.5 mr-1" />
                {preview?.pendingReceiptCount ? `Move ${preview.pendingReceiptCount} item(s) → Inventory` : 'Nothing to move'}
              </Button>
            </>
          )}
          {phase === 'DONE' && (
            <Button size="sm" onClick={onClose}
              className="text-white font-semibold"
              style={{ background: '#1E4E8C' }}
              data-testid="consolidate-done">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
